import * as React from "react";
import type { Artefact } from "@/modules/chat/chat-ui.types";
import { copyArtefactContent } from "@/helpers/clipboard";
import { CopyActionButton } from "@/components/chat/actions/CopyActionButton";
import { useCopyAction } from "@/hooks/useCopyAction";
import { ARTEFACT_FRAME_URL } from "./frame";
import { ExpandButton } from "./ExpandButton";
import { InlineArtefactShell } from "./InlineArtefactShell";
import cssUrl from "@/index.css?url";

interface Props {
  artefact: Artefact;
  onExpand?: (artefactId: string) => void;
}

export const InlineArtefactViewer: React.FC<Props> = React.memo(
  ({ artefact, onExpand }) => {
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const contentRef = React.useRef(artefact.content);
    contentRef.current = artefact.content;
    const [height, setHeight] = React.useState(300);
    const [contentWidth, setContentWidth] = React.useState<number | null>(null);
    const { copied, executeCopy } = useCopyAction();

    React.useEffect(() => {
      const handler = (e: MessageEvent) => {
        if (e.source !== iframeRef.current?.contentWindow) return;
        if (e.data?.type === "artefact-ready") {
          iframeRef.current?.contentWindow?.postMessage(
            { type: "artefact-init", mode: "inline", cssHref: cssUrl, content: contentRef.current },
            "*",
          );
        }
        if (e.data?.type === "artefact-resize") {
          const nextHeight = Number(e.data.height);
          const nextWidth = Number(e.data.width);
          if (Number.isFinite(nextHeight)) {
            setHeight(Math.ceil(nextHeight));
          }
          if (Number.isFinite(nextWidth)) {
            setContentWidth(Math.ceil(nextWidth));
          }
        }
      };
      window.addEventListener("message", handler);
      return () => window.removeEventListener("message", handler);
    }, []);

    React.useEffect(() => {
      iframeRef.current?.contentWindow?.postMessage({ type: "artefact-update", content: artefact.content }, "*");
    }, [artefact.content]);

    const handleCopy = () => executeCopy(() => copyArtefactContent(artefact.content, iframeRef.current));

    return (
      <InlineArtefactShell
        name={artefact.name}
        actions={
          <>
            {onExpand && <ExpandButton artefactId={artefact.id} onExpand={onExpand} />}
            <CopyActionButton copied={copied} onCopy={handleCopy} />
          </>
        }
      >
        <div className="w-full overflow-x-auto overflow-y-hidden">
          <iframe
            ref={iframeRef}
            src={ARTEFACT_FRAME_URL}
            style={{
              width: contentWidth ? `${contentWidth}px` : "100%",
              minWidth: "100%",
              height,
              border: "none",
              display: "block",
            }}
            sandbox="allow-scripts"
          />
        </div>
      </InlineArtefactShell>
    );
  },
  (prev, next) => prev.artefact.id === next.artefact.id && prev.onExpand === next.onExpand,
);
